import { useEffect, useRef, useState } from 'react'
import { IconoQuitar } from '../../componentes/iconos'
import type { ProductoCatalogo } from './catalogo'
import { formatearEuros, formatearFecha } from './formato'
import { compararPrecios, hayDiferencia, totalAnterior, totalBase } from './precios'
import type { Historial, LineaPedido } from './precios'
import type { ProductoElegido } from './SelectorProductos'
import { SelectorProductos } from './SelectorProductos'

/**
 * Las lineas que se van a publicar, con el precio de catalogo **al lado de lo
 * que ese cliente pago la ultima vez**.
 *
 * Es la salvaguarda acordada en el diseno. El precio sale del catalogo de Odoo,
 * y el catalogo esta desfasado: sin editar nada, un BONO 4 pasa de 35 EUR a 32.
 * Que la diferencia se vea aqui, antes de publicar, es lo que separa una
 * decision de un descuido. Cualquier precio se puede corregir a mano.
 *
 * La comparacion es por linea y sobre la **base imponible**, que es lo que
 * guarda Odoo en `price_unit`. El total con IVA del pedido anterior no se
 * enfrenta a nada: compararlo con la base marcaria un 21% de diferencia que no
 * existe.
 */

interface Props {
  lineas: LineaPedido[]
  historial: Historial | undefined
  /** Para poder decir "todavia no ha llegado" en vez de "no hay". */
  cargandoHistorial?: boolean
  onCambiarPrecio: (indice: number, precio: number | null) => void
  /**
   * El catalogo de Odoo, para poder anadirle productos a **este** cliente. Si
   * no llega, el pedido se ensena sin poder tocarlo (es lo que hacia antes).
   */
  catalogo?: ProductoCatalogo[]
  /** Los productos anadidos a mano a este cliente, sin las lineas del backend. */
  anadidas?: LineaPedido[]
  onCambiarAnadidas?: (lineas: LineaPedido[]) => void
}

/** Las lineas anadidas van al final, asi que su indice empieza aqui. */
function primeraAnadida(lineas: LineaPedido[], anadidas: LineaPedido[]): number {
  return lineas.length - anadidas.length
}

function aElegidos(anadidas: LineaPedido[]): ProductoElegido[] {
  return anadidas.map((linea, indice) => ({
    // Una linea sin `product_id` no puede pasar por el selector (no hay
    // producto de catalogo detras), pero tampoco llega aqui: todo lo que se
    // anade en esta pantalla sale del catalogo.
    productoId: linea.product_id ?? -indice - 1,
    nombre: linea.nombre,
    cantidad: linea.cantidad,
    precio: linea.precio,
  }))
}

const CELDA = 'px-3 py-2 text-sm text-tinta-2'
const CABECERA =
  'border-b border-borde px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-tinta-apagada'

/**
 * El precio de una linea, editable.
 *
 * Lleva estado propio y no se ata directamente al valor de fuera porque
 * **borrar el campo es un paso legitimo** para reescribirlo: sin el, vaciar el
 * hueco devolvia el precio del catalogo al instante y lo que se tecleaba a
 * continuacion se anadia detras (32 mas 31,82 daba 3231,82). Mientras el campo
 * esta vacio no hay precio editado, que es lo que el campo esta diciendo.
 */
function CampoPrecio({
  valor,
  etiqueta,
  onCambiar,
}: {
  valor: number
  etiqueta: string
  onCambiar: (precio: number | null) => void
}) {
  const [texto, setTexto] = useState(String(valor))
  const ultimo = useRef(valor)

  useEffect(() => {
    if (valor === ultimo.current) return
    ultimo.current = valor
    setTexto(String(valor))
  }, [valor])

  return (
    <input
      type="number"
      step="0.01"
      aria-label={etiqueta}
      value={texto}
      onChange={(evento) => {
        const escrito = evento.target.value
        setTexto(escrito)
        const numero = Number(escrito)
        ultimo.current = escrito === '' || Number.isNaN(numero) ? valor : numero
        onCambiar(escrito === '' || Number.isNaN(numero) ? null : numero)
      }}
      className="w-24 rounded-lg border border-borde-fuerte bg-superficie px-2 py-1 text-sm text-tinta outline-none focus:border-foco focus:ring-2 focus:ring-foco-halo"
    />
  )
}

function Marca({ children }: { children: string }) {
  return (
    <span className="ml-2 inline-flex rounded-full bg-atencion-fondo px-2 py-0.5 text-xs font-semibold text-atencion-tinta ring-1 ring-atencion-anillo">
      {children}
    </span>
  )
}

export function DetallePedido({
  lineas,
  historial,
  cargandoHistorial,
  onCambiarPrecio,
  catalogo,
  anadidas = [],
  onCambiarAnadidas,
}: Props) {
  const comparadas = compararPrecios(lineas, historial)
  const total = totalBase(lineas)
  const anterior = totalAnterior(historial)
  const diferencias = hayDiferencia(comparadas)
  const desde = primeraAnadida(lineas, anadidas)
  const editable = catalogo !== undefined && onCambiarAnadidas !== undefined

  return (
    <div className="flex flex-col gap-2">
      <div className="overflow-x-auto rounded-xl border border-borde bg-superficie">
        <table aria-label="Líneas del pedido" className="min-w-full border-collapse">
          <thead className="bg-elevado">
            <tr>
              <th scope="col" className={CABECERA}>
                Producto
              </th>
              <th scope="col" className={CABECERA}>
                Cantidad
              </th>
              <th scope="col" className={CABECERA}>
                Precio de catálogo
              </th>
              <th scope="col" className={CABECERA}>
                Lo que pagó la última vez
              </th>
              <th scope="col" className={CABECERA}>
                Subtotal
              </th>
              {editable ? (
                <th scope="col" className={CABECERA}>
                  <span className="sr-only">Quitar</span>
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {comparadas.map((linea, indice) => (
              <tr key={`${linea.nombre}-${indice}`} className="border-b border-borde-suave last:border-b-0">
                <td className={CELDA}>{linea.nombre}</td>
                {/* La devolucion de envases va en negativo: es lo que hace que
                    el pedido cuadre con el flujo actual. Se ensena tal cual. */}
                <td className={CELDA}>{linea.cantidad}</td>
                <td className={CELDA}>
                  <CampoPrecio
                    valor={linea.precio}
                    etiqueta={`Precio de ${linea.nombre}`}
                    onCambiar={(precio) => onCambiarPrecio(indice, precio)}
                  />
                </td>
                <td className={CELDA}>
                  {linea.precioAnterior === null ? (
                    <span className="text-tinta-apagada">
                      {cargandoHistorial ? 'Buscando' : 'Sin dato'}
                    </span>
                  ) : (
                    <>
                      {formatearEuros(linea.precioAnterior)}
                      {linea.difiere ? <Marca>Distinto</Marca> : null}
                    </>
                  )}
                </td>
                <td className={CELDA}>{formatearEuros(linea.subtotal)}</td>
                {editable ? (
                  <td className={CELDA}>
                    {/* Solo se quita lo que se ha anadido aqui. Las lineas que
                        compuso el backend son la estructura del bono: quitarle
                        los envases dejaria un pedido que no cuadra con los que
                        crea hoy el flujo. */}
                    {indice >= desde ? (
                      <button
                        type="button"
                        aria-label={`Quitar ${linea.nombre}`}
                        onClick={() =>
                          onCambiarAnadidas?.(anadidas.filter((_, i) => i !== indice - desde))
                        }
                        className="rounded-lg p-1 text-tinta-apagada transition-colors hover:bg-elevado hover:text-tinta"
                      >
                        <IconoQuitar aria-hidden="true" className="size-4" />
                      </button>
                    ) : null}
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-borde bg-elevado">
              <td className={`${CELDA} font-semibold text-tinta`} colSpan={4}>
                Base imponible del pedido
              </td>
              <td className={`${CELDA} font-semibold text-tinta`}>{formatearEuros(total)}</td>
              {editable ? <td className={CELDA} /> : null}
            </tr>
          </tfoot>
        </table>
      </div>

      {editable ? (
        <SelectorProductos
          catalogo={catalogo}
          elegidos={aElegidos(anadidas)}
          onCambiar={(elegidos) =>
            onCambiarAnadidas?.(
              elegidos.map((elegido) => ({
                nombre: elegido.nombre,
                cantidad: elegido.cantidad,
                precio: elegido.precio,
                product_id: elegido.productoId,
              })),
            )
          }
        />
      ) : null}

      {historial?.encontrado ? (
        <p className="text-sm text-tinta-apagada">
          Último pedido de este cliente: {historial.presupuesto || 'sin número'}
          {historial.fecha ? ` del ${formatearFecha(historial.fecha)}` : ''}
          {anterior === null ? '' : `, ${formatearEuros(anterior)} de base`}
          {historial.confirmado ? '' : ' (seguía en borrador, era una propuesta)'}.
        </p>
      ) : (
        <p className="text-sm text-tinta-apagada">
          {cargandoHistorial
            ? 'Buscando el último pedido de este cliente.'
            : 'Este cliente no tiene ningún pedido anterior con el que comparar.'}
        </p>
      )}

      {diferencias ? (
        <p className="rounded-lg border border-atencion-anillo bg-atencion-fondo px-3 py-2 text-sm text-atencion-tinta">
          Hay precios que no coinciden con lo que este cliente pagó la última vez. El precio sale
          del catálogo, que está por debajo de lo que se viene cobrando. Corrígelo aquí o fija un
          precio pactado en su ficha si no es lo que quieres publicar.
        </p>
      ) : null}
    </div>
  )
}
